import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Form, InputGroup, Row, Card } from 'react-bootstrap';

import {
  FormProvider,
  RHFControl,
  RHFPhoneInput,
  RHFSelect
} from 'components/common/form';
import ReactSelect from 'react-select';
import * as Yup from 'yup';

import {
  getCitiesOptions,
  getCountriesOptions
} from '../../../data/options/countris-and-cities';
import { TITLE_OPTIONS, HEAR_ABOUT_US_OPTIONS } from '../data';

import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import LoadingButton from 'components/common/LoadingButton';

function DonorForm({ form }) {
  const { watch, setValue } = form;

  const country = watch('country');
  const countriesOptions = useMemo(() => getCountriesOptions(), []);
  const citiesOptions = useMemo(() => {
    if (country) {
      return getCitiesOptions(country);
    }
    return [];
  }, [country]);

  const [addressType, setAddressType] = useState('UK_Address');
  const [postCode, setPostCode] = useState('');
  const [addressList, setAddressList] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [addressMenuOpen, setAddressMenuOpen] = useState(false);
  const debounceRef = useRef(null);

  const fetchAddresses = async postCode => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/address-lookup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY
          },
          body: JSON.stringify({ action: 'autocomplete', postCode })
        }
      );

      const data = await response.json();
      setAddressList(data.suggestions || []);
      setAddressMenuOpen(true);
    } catch (error) {
      console.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const addressOptions = addressList?.map(({ address, id }) => ({
    label: address,
    value: id
  }));

  const fetchAddressDetails = async addressId => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/address-lookup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY
          },
          body: JSON.stringify({ action: 'get', addressId })
        }
      );

      const data = await response.json();

      setSelectedAddress(data);
    } catch (error) {
      console.error(error.message);
    }
  };

  const handleManualAddressEntry = () => {
    setAddressType('Non-UK_Address');
    setSelectedAddress(null);
  };

  const handleAddressSelect = e => {
    setAddressMenuOpen(false);
    fetchAddressDetails(e?.value);
  };

  useEffect(() => {
    if (selectedAddress) {
      setValue('addressLine1', selectedAddress.line_1);
      setValue('addressLine2', selectedAddress.line_2);
      setValue('city', selectedAddress.town_or_city);
      setValue('country', 'United Kingdom');
      setValue('postcode', selectedAddress.postcode);
    }
  }, [selectedAddress, setValue]);

  const handlePostCodeChange = e => {
    const value = e.target.value;
    setPostCode(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.length >= 2) {
      debounceRef.current = setTimeout(() => {
        fetchAddresses(value);
      }, 300);
    } else {
      setAddressList([]);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      fetchAddresses(postCode);
    }
  };

  return (
    <FormProvider
      methods={form}
      onSubmit={form.handleSubmit(data => console.log(data))}
    >
      {/* Personal Information Card */}
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">Personal Information</h5>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <RHFSelect
                name="title"
                label="Title"
                options={TITLE_OPTIONS}
                required
              />
            </Col>
            <Col md={6}>
              <RHFSelect
                name="hearAboutUs"
                label="How did you find us?"
                options={HEAR_ABOUT_US_OPTIONS}
                placeholder="Select an option"
                required
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <RHFControl
                name="firstName"
                placeholder="First name"
                label="First name"
                required
              />
            </Col>
            <Col md={6}>
              <RHFControl
                name="lastName"
                placeholder="Last name"
                label="Last name"
                required
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <RHFControl
                name="email"
                type="email"
                placeholder="Email"
                label="Email"
                required
              />
            </Col>
            <Col md={6}>
              <RHFPhoneInput label="Phone" name="phoneNumber" required />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Address Card */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">Address Information</h5>
        </Card.Header>
        <Card.Body>
          <Flex direction="row" gap="2" className="mb-3">
            <Button
              variant={
                addressType === 'UK_Address'
                  ? 'outline-primary'
                  : 'outline-secondary'
              }
              onClick={() => setAddressType('UK_Address')}
            >
              UK Address
            </Button>
            <Button
              variant={
                addressType === 'Non-UK_Address'
                  ? 'outline-primary'
                  : 'outline-secondary'
              }
              onClick={() => setAddressType('Non-UK_Address')}
            >
              Non-UK Address
            </Button>
          </Flex>

          {addressType === 'UK_Address' && (
            <div className="mb-3">
              <Form.Label>
                Post Code <span className="text-danger">*</span>
              </Form.Label>
              <InputGroup className="mb-1">
                <Form.Control
                  type="text"
                  placeholder="Enter Post Code"
                  onChange={handlePostCodeChange}
                  onKeyDown={handleKeyDown}
                />

                <LoadingButton
                  loading={isLoading}
                  variant="primary"
                  onClick={() => fetchAddresses(postCode)}
                >
                  Search
                </LoadingButton>
              </InputGroup>
              <p className="mb-2 wr-donation-helper-text">
                The Post Code search only works for UK, if the billing address
                is outside of the UK, Enter it manually below
              </p>

              {!!addressList.length && (
                <Form.Group className="mb-2">
                  <Form.Label>
                    Select your address <span className="text-danger">*</span>
                  </Form.Label>
                  <ReactSelect
                    options={addressOptions}
                    onChange={handleAddressSelect}
                    menuIsOpen={addressMenuOpen}
                    onMenuOpen={() => setAddressMenuOpen(true)}
                    onMenuClose={() => setAddressMenuOpen(false)}
                    classNamePrefix="react-select"
                    placeholder="Select..."
                  />
                </Form.Group>
              )}
            </div>
          )}

          {(addressType === 'Non-UK_Address' || selectedAddress) && (
            <div className="mb-3">
              <Row className="g-3">
                <Col md="12">
                  <RHFControl
                    name="postcode"
                    label="Post Code"
                    placeholder="add Zip/post Code"
                    required
                  />
                </Col>
                <Col md="12">
                  <RHFControl
                    name="addressLine1"
                    label="First Line Address"
                    placeholder="add Address Line 1"
                    required
                  />
                </Col>
                <Col md="12">
                  <RHFControl
                    name="addressLine2"
                    label="Second Line Address"
                    placeholder="add Address Line 2"
                  />
                </Col>
                <Col md="6">
                  <RHFSelect
                    label="Country"
                    name="country"
                    options={countriesOptions}
                    required
                  />
                </Col>
                <Col md="6">
                  <RHFSelect
                    label="City"
                    name="city"
                    options={citiesOptions}
                    disabled={!country}
                    required
                  />
                </Col>
              </Row>
            </div>
          )}

          {addressType === 'UK_Address' && (
            <IconButton
              icon="plus"
              iconClassName="me-2"
              variant="link"
              className="w-100 mx-auto py-2 text-primary fw-semi-bold"
              onClick={handleManualAddressEntry}
            >
              Enter Address Manually
            </IconButton>
          )}
        </Card.Body>
      </Card>
    </FormProvider>
  );
}

const donorFormDefaultValues = {
  hearAboutUs: '',
  title: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  type: 'personal',
  addressLine1: '',
  addressLine2: '',
  city: '',
  country: '',
  postcode: '',
  isPrimary: true
};

const donorSchema = Yup.object().shape({
  hearAboutUs: Yup.string().required('Required Field'),
  title: Yup.string().required('Required Field'),
  firstName: Yup.string().required('Required Field'),
  lastName: Yup.string().required('Required Field'),

  addressLine1: Yup.string().required('Required Field'),
  addressLine2: Yup.string(),
  city: Yup.string().required('Required Field'),
  country: Yup.string().required('Required Field'),
  postcode: Yup.string().required('Required Field'),
  isPrimary: Yup.boolean().required('Required Field'),

  email: Yup.string().email().required('Required Field'),

  phoneNumber: Yup.string().required('Required Field'),
  type: Yup.string().required('Required Field')
});

DonorForm.propTypes = {
  form: PropTypes.object.isRequired
};

export { DonorForm, donorFormDefaultValues, donorSchema };
